import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

export type NarratorGender = 'male' | 'female';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fired when the user selects a gender AND taps the explicit
   * "Audio erstellen" confirm button. The sheet does NOT auto-close on
   * option pick — the user makes a choice, then confirms.
   */
  onConfirm: (gender: NarratorGender) => void;
}

/**
 * FIX A — Narrator-gender selection sheet for audio retrofit.
 *
 * Bottom-sheet chrome from the shared shadcn Sheet primitive (same
 * mechanics as WorldSelectionSheet / SchlafTimerSheet). Header string
 * and option labels are pulled verbatim from
 * STRINGS.builder.shared.voiceSelectorHeader / voiceMale / voiceFemale
 * so the retrofit picker matches the AA/KP/SB creation-flow voice
 * picker one-to-one (mic emoji in the header, "Männlich" / "Weiblich"
 * capitalization, Check icon on the selected option with border-gold
 * highlight). If the builder ever changes those labels, this sheet
 * follows automatically.
 *
 * Adapted from the builder's VoiceSelector shape in two ways:
 *  1. No default preselection — the confirm button is disabled until a
 *     choice is made.
 *  2. Explicit "Audio erstellen" confirm at the bottom instead of the
 *     builder's implicit "next step" behavior. Dismissing without
 *     confirming is a full no-op (parent sees `onOpenChange(false)`,
 *     never `onConfirm`).
 *
 * Selection state resets on every `open` transition so a re-opened
 * sheet always starts empty.
 */
export function RetrofitGenderSheet({ open, onOpenChange, onConfirm }: Props) {
  const [selected, setSelected] = useState<NarratorGender | null>(null);

  // Reset the selection whenever the sheet is re-opened.
  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm(selected);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl gap-3 pt-3 pb-6 px-4">
        <div aria-hidden className="mx-auto w-8 h-1 rounded-full bg-black/20" />

        <SheetHeader className="p-0 pt-2">
          <SheetTitle className="text-sm font-semibold text-dream-navy">
            {STRINGS.builder.shared.voiceSelectorHeader}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {STRINGS.builder.shared.voiceSelectorHeader}
          </SheetDescription>
        </SheetHeader>

        <div
          role="radiogroup"
          aria-label={STRINGS.builder.shared.voiceSelectorHeader}
          className="grid grid-cols-2 gap-3 pt-2"
        >
          <GenderOption
            label={STRINGS.builder.shared.voiceMale}
            active={selected === 'male'}
            onSelect={() => setSelected('male')}
            testId="retrofit-gender-male"
          />
          <GenderOption
            label={STRINGS.builder.shared.voiceFemale}
            active={selected === 'female'}
            onSelect={() => setSelected('female')}
            testId="retrofit-gender-female"
          />
        </div>

        <Button
          type="button"
          onClick={handleConfirm}
          disabled={selected === null}
          className="mt-2 h-11 w-full bg-dream-gold text-sm font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-50"
          data-testid="retrofit-gender-confirm"
        >
          {STRINGS.retrofit.sheet.confirm}
        </Button>
      </SheetContent>
    </Sheet>
  );
}

interface GenderOptionProps {
  label: string;
  active: boolean;
  onSelect: () => void;
  testId: string;
}

/**
 * Mirrors builder/VoiceSelector.tsx's VoiceOption visual language (h-11,
 * Check icon on the left when active, border-dream-gold on selection)
 * with one deliberate deviation from the builder: the check icon is
 * absolutely positioned so the label sits truly centered in the button.
 * The builder puts the check inline with `flex gap-2 justify-center`,
 * which reserves ~5 rem worth of space via `w-5` even when the icon is
 * `invisible` — pushing the label ~14 px right of the button center.
 * Moving the check to `absolute left-3` frees the flex row to center
 * the label on its own axis; the check overlays the button's left
 * padding without disturbing the layout.
 */
function GenderOption({ label, active, onSelect, testId }: GenderOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      className={cn(
        'relative flex h-11 items-center justify-center rounded-lg text-base font-medium text-dream-navy transition-colors',
        active
          ? 'border-2 border-dream-gold bg-dream-white'
          : 'border-[1.5px] border-dream-navy/10 bg-dream-white/80',
      )}
      data-testid={testId}
    >
      <Check
        aria-hidden
        strokeWidth={3}
        className={cn('absolute left-3 h-5 w-5 text-success', !active && 'invisible')}
      />
      <span>{label}</span>
    </button>
  );
}
